(function (window, document) {
  "use strict";

  var DHCommerce = window.DHCommerce = window.DHCommerce || {};
  var previousServer = DHCommerce.server || {};

  var DEFAULTS = {
    functionName: "get-commerce-dashboard",
    method: "GET",
    timeout: 15000,
    cacheTTL: 45000,
    credentials: "same-origin"
  };

  var state = {
    loading: false,
    inFlight: null,
    cache: null,
    cacheExpiresAt: 0,
    lastLoadedAt: null,
    lastError: null,
    endpoint: null
  };

  function isObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function firstDefined(values, fallback) {
    var i;
    for (i = 0; i < values.length; i += 1) {
      if (values[i] !== undefined && values[i] !== null && values[i] !== "") {
        return values[i];
      }
    }
    return fallback;
  }

  function toNumber(value, fallback) {
    var normalized;
    var parsed;

    if (typeof value === "number" && isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      normalized = value.replace(/[$,%\s,]/g, "");
      parsed = parseFloat(normalized);
      return isFinite(parsed) ? parsed : (fallback || 0);
    }

    return fallback || 0;
  }

  function normalizeRate(value) {
    var number = toNumber(value, 0);
    if (number > 0 && number <= 1) {
      return number * 100;
    }
    return number;
  }

  function getConfig() {
    var config = DHCommerce.config || {};

    if (config && typeof config.all === "function") {
      try {
        return config.all();
      } catch (error) {
        console.warn("[DH Commerce Server] Could not read config through config.all().", error);
      }
    }

    if (config && typeof config.snapshot === "function") {
      try {
        return config.snapshot();
      } catch (snapshotError) {
        console.warn("[DH Commerce Server] Could not read config snapshot.", snapshotError);
      }
    }

    return config;
  }

  function joinUrl(base, path) {
    return String(base || "").replace(/\/+$/, "") + "/" + String(path || "").replace(/^\/+/, "");
  }

  function resolveEndpoint(options) {
    var config = getConfig();
    var endpoints = config.endpoints || {};
    var supabase = config.supabase || {};
    var explicit = firstDefined([
      options && options.endpoint,
      previousServer.endpoint,
      config.dashboardEndpoint,
      endpoints.dashboard,
      DHCommerce.dashboardEndpoint,
      DHCommerce.serverEndpoint
    ], null);
    var baseUrl;
    var functionName;

    if (explicit) {
      return explicit;
    }

    baseUrl = firstDefined([
      supabase.url,
      config.supabaseUrl,
      config.supabaseURL,
      DHCommerce.supabaseUrl,
      DHCommerce.supabaseURL
    ], null);

    functionName = firstDefined([
      options && options.functionName,
      supabase.dashboardFunction,
      config.dashboardFunction,
      endpoints.dashboardFunction,
      DEFAULTS.functionName
    ], DEFAULTS.functionName);

    if (baseUrl) {
      return joinUrl(baseUrl, "functions/v1/" + functionName);
    }

    return null;
  }

  function resolveHeaders(options) {
    var config = getConfig();
    var supabase = config.supabase || {};
    var headers = { "Content-Type": "application/json" };
    var configured = firstDefined([
      options && options.headers,
      config.dashboardHeaders,
      config.headers,
      supabase.headers
    ], {});
    var anonKey = firstDefined([
      options && options.anonKey,
      supabase.anonKey,
      config.supabaseAnonKey,
      config.supabaseKey,
      DHCommerce.supabaseAnonKey
    ], null);
    var key;

    if (isObject(configured)) {
      for (key in configured) {
        if (Object.prototype.hasOwnProperty.call(configured, key)) {
          headers[key] = configured[key];
        }
      }
    }

    if (anonKey) {
      if (!headers.apikey) {
        headers.apikey = anonKey;
      }
      if (!headers.Authorization) {
        headers.Authorization = "Bearer " + anonKey;
      }
    }

    return headers;
  }

  function createError(message, details) {
    var error = new Error(message);
    var key;
    details = details || {};
    for (key in details) {
      if (Object.prototype.hasOwnProperty.call(details, key)) {
        error[key] = details[key];
      }
    }
    return error;
  }

  function parseJsonResponse(response, endpoint) {
    return response.text().then(function (text) {
      var parsed = null;

      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch (parseError) {
          if (response.ok) {
            throw createError("Dashboard response was not valid JSON.", {
              status: response.status,
              endpoint: endpoint,
              response: text,
              cause: parseError
            });
          }
        }
      }

      if (!response.ok) {
        throw createError(
          "Dashboard request failed with status " + response.status + ".",
          {
            status: response.status,
            endpoint: endpoint,
            response: parsed || text || null
          }
        );
      }

      return parsed || {};
    });
  }

  function unwrapPayload(payload) {
    if (!isObject(payload)) {
      return {};
    }
    if (isObject(payload.dashboard)) {
      return payload.dashboard;
    }
    if (isObject(payload.data)) {
      return payload.data;
    }
    return payload;
  }

  function readMetric(source, aliases, fallback) {
    var totals = isObject(source.totals) ? source.totals : {};
    var metrics = isObject(source.metrics) ? source.metrics : {};
    var i;
    var key;

    for (i = 0; i < aliases.length; i += 1) {
      key = aliases[i];
      if (totals[key] !== undefined) {
        return totals[key];
      }
      if (metrics[key] !== undefined) {
        return metrics[key];
      }
      if (source[key] !== undefined) {
        return source[key];
      }
    }

    return fallback;
  }

  function normalizeArray(source, aliases) {
    var i;
    for (i = 0; i < aliases.length; i += 1) {
      if (Array.isArray(source[aliases[i]])) {
        return source[aliases[i]];
      }
    }
    return [];
  }

  function normalizeDashboard(payload) {
    var source = unwrapPayload(payload);
    var sessions = toNumber(readMetric(source, ["sessions", "session_count", "total_sessions"], 0), 0);
    var visitors = toNumber(readMetric(source, ["visitors", "unique_visitors", "visitor_count", "users"], 0), 0);
    var productViews = toNumber(readMetric(source, ["productViews", "product_views", "product_view_count", "views"], 0), 0);
    var categorySelections = toNumber(readMetric(source, ["categorySelections", "category_selections", "category_clicks"], 0), 0);
    var addToCarts = toNumber(readMetric(source, ["addToCarts", "add_to_carts", "add_to_cart", "cart_adds"], 0), 0);
    var checkoutStarts = toNumber(readMetric(source, ["checkoutStarts", "checkout_starts", "checkout_started", "checkouts"], 0), 0);
    var purchases = toNumber(readMetric(source, ["purchases", "purchase_completed", "purchase_count", "orders", "completed_orders"], 0), 0);
    var revenue = toNumber(readMetric(source, ["revenue", "total_revenue", "gross_revenue", "sales"], 0), 0);
    var conversionRate = normalizeRate(readMetric(source, ["conversionRate", "conversion_rate", "cart_conversion_rate"], 0));
    var purchaseRate = normalizeRate(readMetric(source, ["purchaseRate", "purchase_rate", "checkout_conversion_rate"], 0));
    var averageOrderValue = toNumber(readMetric(source, ["averageOrderValue", "average_order_value", "aov"], 0), 0);

    if (!conversionRate && sessions > 0) {
      conversionRate = (addToCarts / sessions) * 100;
    }
    if (!purchaseRate && checkoutStarts > 0) {
      purchaseRate = (purchases / checkoutStarts) * 100;
    }
    if (!averageOrderValue && purchases > 0) {
      averageOrderValue = revenue / purchases;
    }

    return {
      success: payload && payload.success !== false,
      generatedAt: firstDefined([
        source.generatedAt,
        source.generated_at,
        payload && payload.generatedAt,
        payload && payload.generated_at
      ], null),
      period: firstDefined([
        source.period,
        payload && payload.period
      ], null),
      totals: {
        sessions: sessions,
        visitors: visitors,
        productViews: productViews,
        categorySelections: categorySelections,
        addToCarts: addToCarts,
        checkoutStarts: checkoutStarts,
        purchases: purchases,
        revenue: revenue,
        conversionRate: conversionRate,
        purchaseRate: purchaseRate,
        averageOrderValue: averageOrderValue
      },
      events: normalizeArray(source, ["events", "eventSeries", "event_series"]),
      products: normalizeArray(source, ["products", "productPerformance", "product_performance"]),
      categories: normalizeArray(source, ["categories", "categoryPerformance", "category_performance"]),
      devices: normalizeArray(source, ["devices", "deviceBreakdown", "device_breakdown"]),
      shippingTiers: normalizeArray(source, ["shippingTiers", "shipping_tiers", "shippingBreakdown", "shipping_breakdown"]),
      raw: payload || {}
    };
  }

  function request(options) {
    var opts = options || {};
    var endpoint = resolveEndpoint(opts);
    var method = String(firstDefined([opts.method, getConfig().dashboardMethod, DEFAULTS.method], DEFAULTS.method)).toUpperCase();
    var timeout = toNumber(firstDefined([opts.timeout, getConfig().dashboardTimeout, DEFAULTS.timeout], DEFAULTS.timeout), DEFAULTS.timeout);
    var controller = typeof window.AbortController === "function" ? new window.AbortController() : null;
    var timer = null;
    var fetchOptions;

    if (!endpoint) {
      console.warn("[DH Commerce Server] Dashboard endpoint is not configured.");
      return Promise.reject(createError("Dashboard endpoint is not configured.", {
        code: "DH_COMMERCE_ENDPOINT_MISSING"
      }));
    }

    state.endpoint = endpoint;

    fetchOptions = {
      method: method,
      headers: resolveHeaders(opts),
      credentials: firstDefined([opts.credentials, getConfig().dashboardCredentials, DEFAULTS.credentials], DEFAULTS.credentials)
    };

    if (controller) {
      fetchOptions.signal = controller.signal;
      timer = window.setTimeout(function () {
        controller.abort();
      }, timeout);
    }

    if (method !== "GET" && method !== "HEAD") {
      fetchOptions.body = JSON.stringify(opts.body || {});
    }

    return window.fetch(endpoint, fetchOptions)
      .then(function (response) {
        return parseJsonResponse(response, endpoint);
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") {
          throw createError("Dashboard request timed out after " + timeout + "ms.", {
            code: "DH_COMMERCE_TIMEOUT",
            endpoint: endpoint,
            timeout: timeout
          });
        }
        throw error;
      })
      .then(function (result) {
        if (timer) {
          window.clearTimeout(timer);
        }
        return result;
      }, function (error) {
        if (timer) {
          window.clearTimeout(timer);
        }
        throw error;
      });
  }

  function loadDashboard(options) {
    var opts = options || {};
    var ttl = toNumber(firstDefined([opts.cacheTTL, getConfig().dashboardCacheTTL, DEFAULTS.cacheTTL], DEFAULTS.cacheTTL), DEFAULTS.cacheTTL);
    var now = Date.now();

    if (!opts.force && state.cache && state.cacheExpiresAt > now) {
      return Promise.resolve(state.cache);
    }

    if (!opts.force && state.inFlight) {
      return state.inFlight;
    }

    state.loading = true;
    state.lastError = null;

    state.inFlight = request(opts)
      .then(function (payload) {
        var dashboard = normalizeDashboard(payload);
        state.cache = dashboard;
        state.cacheExpiresAt = Date.now() + ttl;
        state.lastLoadedAt = new Date().toISOString();
        state.loading = false;
        state.inFlight = null;
        return dashboard;
      })
      .catch(function (error) {
        state.loading = false;
        state.inFlight = null;
        state.lastError = error;
        console.warn("[DH Commerce Server] Dashboard load failed.", error);
        throw error;
      });

    return state.inFlight;
  }

  function clearCache() {
    state.cache = null;
    state.cacheExpiresAt = 0;
    return true;
  }

  function configure(options) {
    var opts = options || {};
    var configApi = DHCommerce.config || {};
    var overrides = {};

    if (opts.endpoint) {
      overrides.endpoints = { dashboard: opts.endpoint };
    }
    if (opts.method) {
      overrides.dashboardMethod = opts.method;
    }
    if (opts.timeout !== undefined) {
      overrides.dashboardTimeout = opts.timeout;
    }
    if (opts.cacheTTL !== undefined) {
      overrides.dashboardCacheTTL = opts.cacheTTL;
    }
    if (opts.headers) {
      overrides.dashboardHeaders = opts.headers;
    }

    if (configApi && typeof configApi.extend === "function") {
      configApi.extend(overrides);
    } else {
      DHCommerce.config = DHCommerce.config || {};

      if (opts.endpoint) {
        DHCommerce.config.endpoints = DHCommerce.config.endpoints || {};
        DHCommerce.config.endpoints.dashboard = opts.endpoint;
      }
      if (opts.method) {
        DHCommerce.config.dashboardMethod = opts.method;
      }
      if (opts.timeout !== undefined) {
        DHCommerce.config.dashboardTimeout = opts.timeout;
      }
      if (opts.cacheTTL !== undefined) {
        DHCommerce.config.dashboardCacheTTL = opts.cacheTTL;
      }
      if (opts.headers) {
        DHCommerce.config.dashboardHeaders = opts.headers;
      }
    }

    state.endpoint = resolveEndpoint(opts);
    return getStatus();
  }

  function getStatus() {
    return {
      ready: !!resolveEndpoint({}),
      endpoint: resolveEndpoint({}),
      loading: state.loading,
      cached: !!(state.cache && state.cacheExpiresAt > Date.now()),
      cacheExpiresAt: state.cacheExpiresAt ? new Date(state.cacheExpiresAt).toISOString() : null,
      lastLoadedAt: state.lastLoadedAt,
      lastError: state.lastError ? {
        message: state.lastError.message,
        status: state.lastError.status || null,
        code: state.lastError.code || null,
        endpoint: state.lastError.endpoint || null
      } : null
    };
  }

  function health(options) {
    var endpoint = resolveEndpoint(options || {});
    if (!endpoint) {
      return Promise.resolve({
        ok: false,
        configured: false,
        endpoint: null,
        message: "Dashboard endpoint is not configured."
      });
    }

    return request(options || {})
      .then(function () {
        return {
          ok: true,
          configured: true,
          endpoint: endpoint,
          message: "Dashboard endpoint responded successfully."
        };
      })
      .catch(function (error) {
        return {
          ok: false,
          configured: true,
          endpoint: endpoint,
          message: error.message,
          status: error.status || null,
          code: error.code || null
        };
      });
  }

  DHCommerce.server = {
    version: "1.0.0",
    configure: configure,
    request: request,
    loadDashboard: loadDashboard,
    normalizeDashboard: normalizeDashboard,
    clearCache: clearCache,
    health: health,
    getStatus: getStatus
  };

  if (previousServer && isObject(previousServer)) {
    Object.keys(previousServer).forEach(function (key) {
      if (DHCommerce.server[key] === undefined) {
        DHCommerce.server[key] = previousServer[key];
      }
    });
  }

  if (document && document.dispatchEvent && typeof window.CustomEvent === "function") {
    document.dispatchEvent(new window.CustomEvent("dhcommerce:server-ready", {
      detail: DHCommerce.server.getStatus()
    }));
  }
})(window, document);